import {
    Component, ContentChild, EventEmitter, Input, OnInit, Output,
    OnChanges, TemplateRef, OnDestroy, Inject, Optional, SimpleChanges, Renderer2
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { delay, first, takeUntil } from 'rxjs/operators';
import * as Models from './dynamic-grid.interface';
import { PageStateService } from './page-state.service';
import { AdobeLaunchService } from '@ui-app/services/adobe-launch.service';

@Component({
    selector: 'ui-dynamic-grid-by-data',
    templateUrl: './dynamic-grid-by-data.component.html',
    styleUrls: ['./dynamic-grid-by-data.component.less']
})
export class DynamicGridByDataComponent implements OnChanges, OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    @Input() gridRequest: Record<string, any>;
    @Input() gridData: Models.ITableWithFilterModelGeneric<any>;
    @Input() allCount: number;
    @Input() noTilesMode: boolean;
    @Input() pageSizesList: number[] = [1, 10, 25, 50, 100];

    @Input() additionalColumns: Models.ITableMappingDto[] = [];
    @Input() hidePagination: boolean = false;
    @Input() positionDateRange: string = 'left';
    @Input() noInitDataImgPath = '/assets/img/img_no_data.svg';
    @Input() noInitDataText = 'No data to display';
    @Input() noInitDataSubtext = '';
    @Input() rowTooltip: string = '';

    @Input() noDataConfig = {
        image: '/assets/img/img_no_data.svg',
        text: 'No data to display',
        subtext: ''
    };
    @Input() noFilteredDataConfig = {
        image: '/assets/img/no_info_to_display.svg',
        text: 'No results to display',
        subtext: 'Please refine your filter'
    };
    @Input() noSearchedDataConfig = {
        image: '/assets/img/no_search_to_display.svg',
        text: 'No results to display',
        subtext: 'Please, check spelling or refine your search'
    };

    /**
     * design state
     */
    @Input() rowHovered: boolean[] = [];
    @Input() detailsRowHovered: { [key: string]: boolean[] } = {};
    /**
     * CHECKBOXES
     */
    private gridSelectionValue: Record<string, boolean>;
    @Input()
    get gridSelection(): Record<string, boolean> {
        return this.gridSelectionValue;
    }
    set gridSelection(value: Record<string, boolean>) {
        this.gridSelectionValue = value;
        this.gridSelectionChange.emit(this.gridSelectionValue);
    }
    @Output() gridSelectionChange = new EventEmitter();
    @Input() isAllChecked: boolean;
    @Input() isSomeChecked: boolean;
    @Input() isAllDisabled: boolean;
    @Output() toggleAllChecked = new EventEmitter<void>();

    @Output() applySort = new EventEmitter<void>();
    @Output() applyFilter = new EventEmitter<void>();
    @Output() applyPage = new EventEmitter<void>();
    @Output() applyDateRange = new EventEmitter<void>();
    @Output() rowClick = new EventEmitter<any>();

    @Input() fetchDetailsData: (request: any) => Observable<any>;
    @Input() detailsData: { [key: number]: any[] } = {};
    @Input() collapsedRows: Record<number, any> = {};
    @Input() private searchApplied = false;

    tableData: Array<any>;
    tableMetaData: Models.ITableDescription;
    filters: Models.IGridFilters;

    displayColumnFilters = '';
    @Input() isRowClickable = false;
    sortOrder = '';
    sortedColumn = '';
    pagination: Models.PagingInfo = new Models.PagingInfo();

    /**
     * RESTORE STATE
     */
    contentLoaded$ = new BehaviorSubject<boolean>(false);

    @ContentChild('template', { static: true }) transcludeTemplate: TemplateRef<any>;
    @ContentChild('detailsTemplate', { static: true }) transcludeDetailsTemplate: TemplateRef<any>;
    @ContentChild('noInitDataTemplate', { static: true }) noInitDataTemplate: TemplateRef<any>;

    @Input() clearDaterange = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        @Inject(PageStateService) @Optional() private pageStateService: PageStateService,
        private scroller: ViewportScroller,
        private adobeLaunchService: AdobeLaunchService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.gridData && changes.gridData.currentValue) {
            /**
             * discard sorting ui classes
             */
            if (this.gridRequest.sortColumn === '' && (this.sortOrder || this.sortedColumn)) {
                this.sortOrder = '';
                this.sortedColumn = '';
            }

            this.tableMetaData = changes.gridData.currentValue.tableDescription;
            this.tableMetaData.columnsConfig = [...this.tableMetaData.columnsConfig, ...this.additionalColumns];
            this.tableData = this.edgeDataAdapter(changes.gridData.currentValue.data);
            this.filters = changes.gridData.currentValue.filters;
            
            // DEBUG: Log what tableData contains after adapter
            console.log('🔧 ui-dynamic-grid ngOnChanges:', {
                tableDataLength: this.tableData?.length,
                firstRow: this.tableData?.[0],
                columnsConfigLength: this.tableMetaData?.columnsConfig?.length,
                visibleColumns: this.tableMetaData?.columnsConfig?.filter(c => !c.isHidden).map(c => c.columnName)
            });

            if (this.searchApplied) {
                this.tableData.forEach((row) => {
                    this.collapsedRows[row.requestId] = false;
                    this.collapse(row);
                });
            } else {
                this.collapsedRows = {};
                this.detailsData = {};
            }

            this.getSortOrder();
            this.pagination = new Models.PagingInfo(this.gridRequest.pageSize, this.gridRequest.pageIndex, this.tableMetaData.count);

            /**
             * RESTORE STATE
             * should be invoked once
             */
            // if (!changes.gridData.previousValue) {
            //     combineLatest([
            //         this.pageStateService.select(this.pageStateService.selectors.restoreState),
            //         this.pageStateService.select(this.pageStateService.selectors.scrollPosition),
            //         this.pageStateService.select(this.pageStateService.selectors.gridSelection),
            //     ])
            //     .pipe(first(), takeUntil(this.unsubscribe$))
            //     .subscribe(data => this.restoreView(data));
            // }
        }
    }

    private restoreView([restoreState, scrollPosition, gridSelection]:
        [boolean, [number, number], Record<string, boolean>]): void {

        if (restoreState) {
            this.restoreDetailsView();
            this.restoreScrollPosition(scrollPosition);
            this.restoreGridSelection(gridSelection);
            // Clear restoreState flag
            this.pageStateService.dispatch(this.pageStateService.selectors.restoreState, false);

            this.getSortOrder();
        }
    }

    private getSortOrder(): void {
        if (this.gridRequest.sortColumn) {
            const parts = this.gridRequest.sortColumn.split(' ');
            const sortOrder = parts.pop();
            this.sortedColumn = parts.join(' ');
            this.sortOrder = sortOrder === Models.SortOrder.ASC ? Models.SortOrder.ASC : Models.SortOrder.DESC;
        }
    }

    private restoreDetailsView(): void {
        if (this.gridRequest.requestIds.length) {
            this.gridRequest.requestIds.forEach((id: string) => this.collapsedRows[id] = true);
            this.getDetailsData();
        } else {
            this.contentLoaded$.next(true);
        }
    }

    private restoreScrollPosition(scrollPosition: [number, number]): void {
        this.contentLoaded$
            .pipe(
                delay(50),
                first(Boolean),
            )
            .subscribe(() => this.scroller.scrollToPosition(scrollPosition));
    }

    private restoreGridSelection(gridSelection: Record<string, boolean>): void {
        if (this.gridSelection) {
            Object.assign(this.gridSelection, gridSelection);
        }
    }

    ngOnInit(): void {
        this.isRowClickable = this.isRowClickable && this.rowClick.observers.length > 0;
    }

    /**
     * STATE RESTORE
     */
    ngOnDestroy(): void {
        // if (this.pageStateService.isSaveRequired(this.router.routerState.snapshot.url)) {
        //     this.pageStateService.dispatch(this.pageStateService.selectors.restoreState, false);
        //     this.pageStateService.dispatch(this.pageStateService.selectors.gridRequest, this.gridRequest);
        //     this.pageStateService.dispatch(this.pageStateService.selectors.scrollPosition, this.scroller.getScrollPosition());
        // } else {
        //     this.pageStateService.clear(this.pageStateService.selectors.restoreState);
        //     this.pageStateService.clear(this.pageStateService.selectors.gridRequest);
        //     this.pageStateService.clear(this.pageStateService.selectors.scrollPosition);
        // }

        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.unsubscribe$ = null;
    }

    /* HEADER section */

    isColumnSorted(sortName: string): boolean {
        return this.stringContainsWord(this.gridRequest.sortColumn, sortName);
    }

    isColumnFiltered(columnName: string): boolean {
        return !!this.gridRequest.columnFilters[columnName];
    }

    showColumnFilters(columnName: string): void {
        this.displayColumnFilters = columnName;
    }

    isAscOrder(column: Models.ITableMappingDto): boolean {
        return this.sortedColumn === column.sortName && this.sortOrder === Models.SortOrder.ASC;
    }

    get visibleColumnsCount(): number {
        return this.tableMetaData.columnsConfig
            ? this.tableMetaData.columnsConfig.filter((column: Models.ITableMappingDto) => !column.isHidden).length
            : 1;
    }

    get columnWidth(): string {
        return Math.floor(100 / this.visibleColumnsCount) + '%';
    }

    toggleColumnSort(column: Models.ITableMappingDto): void {
        if (this.sortedColumn === column.sortName) {
            this.sortOrder = this.sortOrder === Models.SortOrder.ASC ? Models.SortOrder.DESC : Models.SortOrder.ASC;
        } else {
            this.sortedColumn = column.sortName;
            this.sortOrder = Models.SortOrder.ASC;
        }

        this.gridRequest.sortColumn = this.sortedColumn + ' ' + this.sortOrder;

        this.resetPagingState();
        this.applySort.emit();
        this.adobeLaunchService.trackSort(column.displayName || column.columnName, this.sortOrder);

        /**
         * @todo
         */
        // this.sortDetailsData();
    }

    applyColumnFilters(appliedFilters: Array<string>, columnName: string): void {
        this.displayColumnFilters = null;
        if (!appliedFilters) {
            return;
        } else if (appliedFilters.length) {
            this.gridRequest.columnFilters[columnName] = appliedFilters;
        } else {
            delete this.gridRequest.columnFilters[columnName];
        }

        this.resetPagingState();
        this.applyFilter.emit();
        this.adobeLaunchService.trackFilter(this.getColumnDisplayName(columnName), this.getFilterValueLabels(columnName, appliedFilters));
    }

    /* other */

    onPaginationChange(pagingInfo: Models.PagingInfo): void {
        this.gridRequest.pageIndex = pagingInfo.pageIndex;
        this.gridRequest.pageSize = pagingInfo.pageSize;

        this.applyPage.emit();
    }

    resetPagingState(): void {
        this.pagination = new Models.PagingInfo(this.pagination.pageSize, 1, this.pagination.totalItems);
        this.gridRequest.pageIndex = 1;
    }

    handleRowClick(event: Event, row: any): void {
        this.isRowClickable ? this.rowClick.emit(row) : event.stopPropagation();
    }

    /**
     * EXPANDED ROWS
     */
    isExpandedRow(row: any): boolean {
        return !!this.collapsedRows[row.requestId];
    }

    collapse(row: any): void {
        if (!this.collapsedRows[row.requestId]) {
            this.collapsedRows[row.requestId] = true;

            this.gridRequest.requestIds = [row.requestId];
            this.getDetailsData();
        } else {
            this.collapsedRows[row.requestId] = false;
        }
    }

    private getDetailsData(): void {
        this.fetchDetailsData(this.gridRequest)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((response: Models.ITableWithFilterModelDataListGeneric<any>) => {
                this.gridRequest.requestIds.forEach((requestId: number) => {
                    this.detailsData[requestId] = response.dataList.find(v => parseInt(v.key, 10) === requestId).data;
                });
                this.contentLoaded$.next(true);
            });
    }

    /* LAYOUT logic */

    trackByColumn(index: number, column: Models.ITableMappingDto): string {
        if (!column) {
            return null;
        }
        return column.columnName; // column id
    }

    trackByRow(index: number, row: any): string {
        return row && this.tableMetaData
            ? row[this.tableMetaData.idFieldName]
            : null;
    }

    isColumnVisible(row: any, column: Models.ITableMappingDto): boolean {
        return !column.isHidden;
    }

    dateRangeFilterChanged(dateRangeOutput: Models.DateRangePickerOutput): void {
        dateRangeOutput.dateRange.length
            ? this.gridRequest.columnFilters[dateRangeOutput.columnName] = dateRangeOutput.dateRange
            : delete this.gridRequest.columnFilters[dateRangeOutput.columnName];

        this.resetPagingState();
        this.applyDateRange.emit();
        this.adobeLaunchService.trackFilter(this.getColumnDisplayName(dateRangeOutput.columnName), dateRangeOutput.dateRange.join(' - '));
    }

    isFilterApplied(): boolean {
        return Boolean(Object.keys(this.gridRequest.columnFilters).length);
    }

    isSearchApplied(): boolean {
        return this.gridRequest.textFilter && this.gridRequest.textFilter.length > 0;
    }

    isFiltersOrSearchApplied(): boolean {
        return Boolean(Object.keys(this.gridRequest.columnFilters).length)
            || (this.gridRequest.textFilter && this.gridRequest.textFilter.length > 0);
    }

    recordsExists(): boolean {
        if (this.noTilesMode) {
            return Boolean(this.tableMetaData.count) || (Boolean(!this.tableMetaData.count)
                && this.isFiltersOrSearchApplied());
        }
        return this.allCount > 0;
    }

    private edgeDataAdapter(data: any[]): any[] {
        return data.map((v: any) => {
            Object.keys(v).forEach(i => v[i] = v[i] === null ? '' : v[i]);
            return v;
        });
    }

    private getColumnDisplayName(columnName: string): string {
        const column = this.tableMetaData?.columnsConfig?.find((c: Models.ITableMappingDto) => c.columnName === columnName);
        return column?.displayName || columnName;
    }

    /**
     * Maps raw filter keys (enum/id values used for the backend request) to the human-readable
     * labels actually displayed to the user in the filter checkbox list, for analytics tracking.
     */
    private getFilterValueLabels(columnName: string, appliedFilters: Array<string>): string {
        const filterOptions = this.filters?.[columnName];
        return appliedFilters
            .map((key: string) => {
                const option = filterOptions?.find((opt: Record<string, string>) => opt.key === key);
                return option?.value ?? key;
            })
            .join(', ');
    }

    /**
     * Does the requested string contain the full word
     */
    private stringContainsWord(str: string, word: string): boolean {
        return str.match(new RegExp(`${'\\b'}${word}${'\\b'}`)) != null;
    }
}
